import { lazy } from 'react';
import { Navigate, type RouteObject } from 'react-router-dom';
import AuthGuard from '@/components/feature/AuthGuard';
import AuthLayout from '@/pages/auth/components/AuthLayout';

const Splash = lazy(() => import('@/pages/auth/splash/page'));
const Onboarding = lazy(() => import('@/pages/auth/onboarding/page'));
const Login = lazy(() => import('@/pages/auth/login/page'));
const Signup = lazy(() => import('@/pages/auth/signup/page'));
const OTP = lazy(() => import('@/pages/auth/otp/page'));
// The two reset screens are real now — `POST /auth/password-reset` (a platform-issued code fanned
// out to SMS and email) and `POST /auth/password-reset/confirm` (code + new password, no tokens).
// Their earlier versions were parked unrouted because they fabricated success: a `setTimeout`
// announcing "code sent" with no network call, and a `?token=` link the backend never issued.
// `forgot-mpin` is gone — MPIN exists in no service.
//
// A saver who signs in by OTP never needs these; they serve the email/password mode on the
// sign-in screen, and a saver who wants a password can acquire one here (the code proves SIM
// possession, exactly as the login OTP does).
const ForgotPassword = lazy(() => import('@/pages/auth/forgot-password/page'));
const ResetPassword = lazy(() => import('@/pages/auth/reset-password/page'));
const Lockout = lazy(() => import('@/pages/auth/lockout/page'));
const Home = lazy(() => import('@/pages/home/page'));
const Schemes = lazy(() => import('@/pages/schemes/page'));
const Pay = lazy(() => import('@/pages/pay/page'));
// The Marketplace tab (network-mfe-design §8.5, §9 B14). Entitled by `app.marketplace`, which the
// page itself checks — the route exists unconditionally and redirects home when the flag is off,
// because a route that vanished with a flag would 404 a saver whose branding was slow to resolve.
const Marketplace = lazy(() => import('@/pages/marketplace/page'));
const Spin = lazy(() => import('@/pages/spin/page'));
const SpinGold = lazy(() => import('@/pages/spin-gold/page'));
const Account = lazy(() => import('@/pages/account/page'));
const Profile = lazy(() => import('@/pages/account/profile/page'));
const Ledger = lazy(() => import('@/pages/account/ledger/page'));
const Goals = lazy(() => import('@/pages/account/goals/page'));
const Loans = lazy(() => import('@/pages/account/loans/page'));
const Redemptions = lazy(() => import('@/pages/account/redemptions/page'));
const Payments = lazy(() => import('@/pages/account/payments/page'));
const Notifications = lazy(() => import('@/pages/account/notifications/page'));
const Settings = lazy(() => import('@/pages/account/settings/page'));
const Complaints = lazy(() => import('@/pages/account/complaints/page'));
const Help = lazy(() => import('@/pages/account/help/page'));
const NotFound = lazy(() => import('@/pages/NotFound'));

const routes: RouteObject[] = [
  {
    path: '/',
    element: <Navigate to="/home" replace />,
  },
  {
    path: '/auth',
    element: <AuthLayout />,
    children: [
      { path: 'splash', element: <Splash /> },
      { path: 'onboarding', element: <Onboarding /> },
      { path: 'login', element: <Login /> },
      { path: 'signup', element: <Signup /> },
      { path: 'otp', element: <OTP /> },
      { path: 'forgot-password', element: <ForgotPassword /> },
      { path: 'reset-password', element: <ResetPassword /> },
      { path: 'lockout', element: <Lockout /> },
    ],
  },
  {
    path: '/home',
    element: <AuthGuard><Home /></AuthGuard>,
  },
  {
    path: '/schemes',
    element: <AuthGuard><Schemes /></AuthGuard>,
  },
  {
    path: '/pay',
    element: <AuthGuard><Pay /></AuthGuard>,
  },
  {
    path: '/marketplace',
    element: <AuthGuard><Marketplace /></AuthGuard>,
  },
  {
    path: '/spin',
    element: <AuthGuard><Spin /></AuthGuard>,
  },
  {
    path: '/spin-gold',
    element: <AuthGuard><SpinGold /></AuthGuard>,
  },
  {
    path: '/account',
    element: <AuthGuard><Account /></AuthGuard>,
  },
  {
    path: '/account/profile',
    element: <AuthGuard><Profile /></AuthGuard>,
  },
  {
    path: '/account/ledger',
    element: <AuthGuard><Ledger /></AuthGuard>,
  },
  {
    path: '/account/goals',
    element: <AuthGuard><Goals /></AuthGuard>,
  },
  {
    path: '/account/loans',
    element: <AuthGuard><Loans /></AuthGuard>,
  },
  {
    path: '/account/redemptions',
    element: <AuthGuard><Redemptions /></AuthGuard>,
  },
  {
    path: '/account/payments',
    element: <AuthGuard><Payments /></AuthGuard>,
  },
  {
    path: '/account/notifications',
    element: <AuthGuard><Notifications /></AuthGuard>,
  },
  {
    path: '/account/settings',
    element: <AuthGuard><Settings /></AuthGuard>,
  },
  {
    path: '/account/complaints',
    element: <AuthGuard><Complaints /></AuthGuard>,
  },
  {
    path: '/account/help',
    element: <AuthGuard><Help /></AuthGuard>,
  },
  {
    path: '*',
    element: <NotFound />,
  },
];

export default routes;